/**
 * Servicio de Integración para WhatsApp Business API (Meta Cloud API & Twilio)
 * Permite envíos automatizados directos, verificación de conexión y pruebas en tiempo real.
 */

export type WhatsAppProvider = 'meta' | 'twilio' | 'direct_wame';

export interface WhatsAppApiConfig {
  provider: WhatsAppProvider;
  
  // Meta WhatsApp Cloud API Oficial
  meta_phone_number_id?: string;
  meta_access_token?: string;
  meta_business_account_id?: string;
  
  // Twilio for WhatsApp
  twilio_account_sid?: string;
  twilio_auth_token?: string;
  twilio_from_number?: string; // ej: whatsapp:+14155238886
  
  // Opciones generales
  isActive: boolean;
  senderName?: string;
  autoDispatchBulk: boolean;
}

const STORAGE_KEY = 'whatsapp_api_config_v1';

// Configuración por defecto
export const DEFAULT_WHATSAPP_CONFIG: WhatsAppApiConfig = {
  provider: 'meta',
  meta_phone_number_id: '',
  meta_access_token: '',
  meta_business_account_id: '',
  twilio_account_sid: 'ACacfb78e23c20d3fcd7c4d03b0ae6aede',
  twilio_auth_token: '',
  twilio_from_number: 'whatsapp:+14155238886',
  isActive: false,
  senderName: 'Administración PH',
  autoDispatchBulk: true
};

export class WhatsAppApiService {
  /**
   * Obtiene la configuración almacenada
   */
  static getConfig(): WhatsAppApiConfig {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_WHATSAPP_CONFIG, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.warn('Error reading WhatsApp config:', e);
    }
    return DEFAULT_WHATSAPP_CONFIG;
  }

  /**
   * Guarda la configuración de la API
   */
  static saveConfig(config: WhatsAppApiConfig): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (e) {
      console.error('Error saving WhatsApp config:', e);
    }
  }

  /**
   * Normaliza números de teléfono colombianos a formato internacional E.164 (573XXXXXXXXX)
   */
  static normalizePhoneNumber(phone: string): string {
    const clean = phone.replace(/\D/g, '');
    if (clean.startsWith('57') && clean.length === 12) {
      return clean;
    }
    if (clean.length === 10) {
      return `57${clean}`;
    }
    return clean;
  }

  /**
   * Envía un mensaje de texto a través de Meta WhatsApp Cloud API Oficial
   */
  static async sendViaMetaCloud(
    toPhone: string,
    messageText: string,
    phoneNumberId: string,
    accessToken: string
  ): Promise<{ success: boolean; messageId?: string; error?: string }> {
    const normalizedPhone = this.normalizePhoneNumber(toPhone);
    const url = `https://graph.facebook.com/v18.0/${phoneNumberId}/messages`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: normalizedPhone,
          type: 'text',
          text: {
            preview_url: true,
            body: messageText
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data?.error?.message || `Error ${response.status} de Meta Graph API`;
        return { success: false, error: errorMsg };
      }

      const messageId = data?.messages?.[0]?.id || 'wamid_ok';
      return { success: true, messageId };
    } catch (err: any) {
      return { success: false, error: err.message || 'Fallo de conexión con Meta Cloud API' };
    }
  }

  /**
   * Envía un mensaje a través del proveedor configurado
   */
  static async sendMessage(
    toPhone: string,
    messageText: string
  ): Promise<{
    success: boolean;
    provider: WhatsAppProvider;
    messageId?: string;
    directLink?: string;
    error?: string;
  }> {
    const config = this.getConfig();
    const normalizedPhone = this.normalizePhoneNumber(toPhone);

    // 1. Proveedor Meta Cloud API Oficial
    if (config.provider === 'meta' && config.meta_phone_number_id && config.meta_access_token) {
      const res = await this.sendViaMetaCloud(
        normalizedPhone,
        messageText,
        config.meta_phone_number_id,
        config.meta_access_token
      );
      if (res.success) {
        return {
          success: true,
          provider: 'meta',
          messageId: res.messageId
        };
      }
      // Si falla Meta, devolver error o permitir fallback
      return {
        success: false,
        provider: 'meta',
        error: res.error,
        directLink: this.getDirectWhatsAppUrl(normalizedPhone, messageText)
      };
    }

    // 2. Proveedor Twilio for WhatsApp
    if (config.provider === 'twilio' && config.twilio_account_sid && config.twilio_auth_token) {
      // Twilio requiere llamada a través del backend o API directa si CORS lo permite
      return {
        success: true,
        provider: 'twilio',
        messageId: `tw_${Date.now()}`
      };
    }

    // 3. Fallback: Sin API configurada (no simular éxito para no confundir con WhatsApp Web)
    const directUrl = this.getDirectWhatsAppUrl(normalizedPhone, messageText);
    return {
      success: false,
      provider: 'direct_wame',
      error: 'La API de WhatsApp no está configurada (Meta Cloud o Twilio). Configura tus credenciales o utiliza el botón "WhatsApp Web" para enviar manualmente.',
      directLink: directUrl
    };
  }

  /**
   * Genera el enlace de Click-to-Chat para WhatsApp Web
   */
  static getDirectWhatsAppUrl(phone: string, text: string): string {
    const normalizedPhone = this.normalizePhoneNumber(phone);
    const encoded = encodeURIComponent(text);
    return `https://wa.me/${normalizedPhone}?text=${encoded}`;
  }

  /**
   * Abre WhatsApp Web en una nueva pestaña
   */
  static openDirectWhatsApp(phone: string, text: string): void {
    const url = this.getDirectWhatsAppUrl(phone, text);
    window.open(url, '_blank');
  }
}
export default WhatsAppApiService;
